import { inject, Injectable, ProviderToken } from '@angular/core';
import {
    BehaviorSubject,
    forkJoin,
    from,
    merge,
    Observable,
    of,
    pairwise,
    shareReplay,
    Subject,
    switchMap,
    throwError,
    withLatestFrom,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, tap, map, filter, startWith, take } from 'rxjs/operators';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { EntData } from '@bazis/shared/models/srv.types';
import { Router } from '@angular/router';
import { BazisStorageService } from '@bazis/shared/services/storage.service';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { CookieService } from 'ngx-cookie-service';
import { BazisCryptoService } from '@bazis/signature/crypto/crypto.service';
import jwt_decode from 'jwt-decode';
import { BazisModalService } from '@bazis/shared/services/modal.service';
import { BazisToastService } from '@bazis/shared/services/toast.service';

const ROLE_TOKEN = 'ROLE' as unknown as ProviderToken<{ [index: string]: string }>;
const ROLE_ACCOUNT_TYPE_TOKEN = 'ROLE_ACCOUNT_TYPE' as unknown as ProviderToken<
    Record<string, unknown>
>;

/**
 * **BazisAuthService**
 *
 * Authentication and user session service.
 * Manages access token storage, loads current user and related role/organization data,
 * and provides helpers for login, logout, role change, and sign-up flows.
 *
 * This service is designed to be used as a single source of truth for auth-related
 * state in the application via RxJS streams.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisAuthService {
    protected entityService = inject(BazisEntityService);

    protected storageService = inject(BazisStorageService);

    protected srv = inject(BazisSrvService);

    protected http = inject(HttpClient);

    protected router = inject(Router);

    protected cookieService = inject(CookieService);

    protected cryptoService = inject(BazisCryptoService);

    protected modalService = inject(BazisModalService);

    protected toastService = inject(BazisToastService);

    public role: { [index: string]: string } = inject(ROLE_TOKEN) || {};

    public roleAccountType = inject(ROLE_ACCOUNT_TYPE_TOKEN) || {};

    /** Router path to the login page. */
    authPageUrl = '/login';

    /** Router path used after successful invite flow. */
    successInvitePageUrl = '/success-invite';

    /** Router path used after successful login flow. */
    successLoginPageUrl = '/success-login';

    /** Router path to the personal information page. */
    personalInfoPageUrl = '/profile/personal-info';

    /** Entity type string for the user entity in the backend. */
    userEntityType = 'authing.user';

    /**
     * Current access token holder.
     *
     * This wraps a string token value and is updated by `calculateToken()`,
     * `updateToken()` and `resetToken()`.
     */
    token = new TemplateObservable('', false);

    /**
     * Internal trigger to refresh `user$` stream.
     *
     * Values:
     * - `undefined`: initial state (filtered out)
     * - `null`/empty string: treat as “no user”
     * - string: used to start auth request flow
     * - `EntData`: shortcut to set user without requesting
     */
    protected updateUser$ = new BehaviorSubject(undefined);

    /**
     * Internal trigger to refresh organization data.
     * Used by `updatingOrganization$`.
     */
    protected updateOrganization$ = new Subject();

    /** Flag set while logout request is in progress. */
    isLogouting = false;

    /**
     * If true, organization info is resolved via `organization.organization_info`.
     * If false, organization is resolved via `organization.organization`.
     */
    useOrganizationInfo = true;

    /**
     * Cached request for current user entity.
     * This avoids duplicate entity loading for multiple subscribers.
     */
    protected userRequest$: Observable<EntData> = null;

    /**
     * Organization selection actions provided by backend when user must select organization.
     * Filled when auth request returns NEXT/SELECT_ORGANIZATION error.
     */
    orgList$ = new BehaviorSubject(null);

    /**
     * Trigger to start role change flow.
     * Consumed by `changingRole$`.
     */
    changeRole$ = new Subject();

    /**
     * Internal guard flag to avoid reacting to role changes while role change is in progress.
     */
    protected _changingRole = false;

    /**
     * Emits `true` while auth/user loading is in progress.
     * Set by `user$` flow and cleared when `role$` resolves.
     */
    authing$ = new BehaviorSubject(false);

    /**
     * When set, login page can redirect back to this URL after successful auth.
     */
    urlToRedirectAfterAuth: string = null;

    /**
     * Authentication request stream.
     *
     * Side effects:
     * - Stores access token in localStorage under `access_token`
     * - Updates `token` holder
     * - On SELECT_ORGANIZATION errors, fills `orgList$` and navigates to `/login/organization`
     * - On other errors, resets token
     *
     * @returns Observable of auth response or `null` on error.
     */
    auth$ = this.authRequest$().pipe(
        tap((response) => {
            if (!this.token._ && response.token) {
                localStorage.setItem('access_token', response.token);
                this.userRequest$ = null;
                this.token.set(response.token);
            }
        }),
        catchError((e) => {
            const selectOrg: any = e?.error?.errors?.find(
                (v) => v.code === 'NEXT' || v.code === 'SELECT_ORGANIZATION',
            );
            if (selectOrg) {
                this.orgList$.next(selectOrg.meta.actions);
                this.router.navigate(['/login/organization']);
            } else {
                this.resetToken();
            }
            return of(null);
        }),
    );

    /**
     * Current user entity stream.
     *
     * Workflow:
     * - Wait for `updateUser$` trigger
     * - If trigger is a string: run `auth$` to get auth result
     * - Load user entity from `BazisEntityService.getEntity$`
     * - Cache request with `shareReplay(SHARE_REPLAY_SETTINGS)`
     * - Reset token on errors and map to `null`
     *
     * Special triggers:
     * - `needUpdateUser('socket')`: forces reload from API
     * - `needUpdateUser('role')`: reload after role change
     *
     * @returns Observable of user `EntData` or `null`.
     */
    user$: Observable<EntData> = merge(this.updateUser$).pipe(
        filter((v) => v !== undefined),
        tap((v) => {
            this.authing$.next(true);
        }),
        switchMap((result) => {
            return result === null || result === ''
                ? of(null)
                : typeof result === 'string' || result instanceof String
                  ? this.auth$.pipe(shareReplay(1))
                  : of(result);
        }),
        withLatestFrom(this.updateUser$),
        switchMap(([data, updateUser]) => {
            if ((!this.userRequest$ && data) || updateUser === 'socket' || updateUser === 'role') {
                this.userRequest$ = this.entityService
                    .getEntity$(this.userEntityType, data.user_id, {
                        forceLoad: updateUser === 'socket',
                    })
                    .pipe(
                        map((r) => {
                            this.cookieService.delete('bazis_auth', '/');
                            return r;
                        }),
                        catchError((error) => {
                            this.resetToken();
                            return of(null);
                        }),
                        shareReplay(SHARE_REPLAY_SETTINGS),
                    );
            }
            return data ? this.userRequest$ : of(null);
        }),
        catchError((error) => {
            return of(null);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Emits user id whenever user changes (including login/logout).
     *
     * @returns Observable of user id string or `null` when user is not available.
     */
    userId$: Observable<string> = this.user$.pipe(
        startWith(undefined),
        pairwise(),
        filter(([prevUser, currentUser]) =>
            !!prevUser && !!currentUser
                ? prevUser.id !== currentUser.id
                : !currentUser || prevUser !== currentUser,
        ),
        map(([prevUser, user]) => (user ? user.id : null)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Current role entity stream, resolved from `user.$snapshot.role_current`.
     *
     * @returns Observable of role `EntData` or `null`.
     */
    roleData$: Observable<EntData> = this.user$.pipe(
        startWith(undefined),
        pairwise(),
        switchMap(([prevUser, currentUser]) =>
            currentUser
                ? this.entityService.getEntity$(
                      currentUser.$snapshot.role_current.type,
                      currentUser.$snapshot.role_current.id,
                  )
                : of(null),
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Current role slug stream.
     *
     * Uses `role.$snapshot.slug` when role entity is present.
     * Falls back to `role.anonymous` from injected ROLE map.
     *
     * Side effect: sets `authing$` to `false` when role is resolved.
     *
     * @returns Observable of role slug string.
     */
    role$: Observable<string> = this.roleData$.pipe(
        map((role: EntData) => (role ? role.$snapshot.slug : this.role.anonymous)),
        tap(() => {
            this.authing$.next(false);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Indicates whether current role is a system role.
     *
     * @returns Observable of boolean.
     */
    isSystemRole$: Observable<boolean> = this.roleData$.pipe(
        map((role: EntData) => (role ? role.$snapshot.is_system : false)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Account type derived from role slug using injected ROLE_ACCOUNT_TYPE map.
     *
     * Emits only when role is truthy and no role change is in progress.
     *
     * @returns Observable of account type string. Falls back to `'none'`.
     */
    accountType$ = this.role$.pipe(
        filter((v) => !!v && !this._changingRole),
        map((role) => {
            return this.roleAccountType[role] || 'none';
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Current organization id stream from user snapshot.
     *
     * @returns Observable of organization id string or `null`.
     */
    organizationId$ = this.user$.pipe(
        startWith(undefined),
        pairwise(),
        filter(([prevUser, currentUser]) =>
            !!prevUser && !!currentUser
                ? prevUser.$snapshot.organization?.id !== currentUser.$snapshot.organization?.id
                : prevUser !== currentUser,
        ),
        map(([prevUser, currentUser]) =>
            currentUser ? currentUser.$snapshot.organization?.id : null,
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Organization entity/info stream for current organization id.
     *
     * Delegates to `getOrganizationInfo$()` and shares the latest value.
     *
     * @returns Observable of organization `EntData` or `null`.
     */
    organizationInfo$ = this.organizationId$.pipe(
        switchMap((organizationId) => this.getOrganizationInfo$(organizationId)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Emits id of organization info entity when organization info changes.
     *
     * @returns Observable of organization info id string or `null`.
     */
    organizationInfoId$: Observable<string> = merge(this.organizationInfo$).pipe(
        startWith(undefined),
        pairwise(),
        filter(([prevOrgInfo, currentOrgInfo]) =>
            !!prevOrgInfo && !!currentOrgInfo
                ? prevOrgInfo.id !== currentOrgInfo.id
                : prevOrgInfo !== currentOrgInfo,
        ),
        map(([prevOrgInfo, currentOrgInfo]) => currentOrgInfo),
        map((organizationInfoId: EntData) => (organizationInfoId ? organizationInfoId.id : null)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Full organization info entity stream.
     *
     * Uses `organizationInfoId$` to load `organization.organization_info`
     * when `useOrganizationInfo` is true.
     *
     * @returns Observable of organization info `EntData` or `null`.
     */
    organizationInfoFull$: Observable<EntData> = this.organizationInfoId$.pipe(
        switchMap((organizationInfoId: string) =>
            this.getOrganizationFullInfo$(organizationInfoId),
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Side-effect stream that changes current user role on backend.
     *
     * Steps:
     * - Update user entity (`role_current`)
     * - Clear organization cache in storage
     * - Store `roleChanged` event in localStorage
     * - Recalculate token and redirect to home page
     *
     * Errors are rethrown to subscribers.
     *
     * @returns Observable of update response (depends on backend).
     */
    changingRole$ = this.changeRole$.pipe(
        switchMap(({ roleId, userId }) => {
            this._changingRole = true;
            return this.entityService.updateEntity$(
                this.userEntityType,
                userId,
                {},
                {
                    role_current: roleId,
                },
            );
        }),
        withLatestFrom(this.organizationId$, this.organizationInfoId$),
        tap(([user, orgId, orgInfoId]) => {
            this._changingRole = false;
            this.clearOrganizationInStorage(orgId, orgInfoId);
            localStorage.setItem(
                'event',
                JSON.stringify({ eventName: 'roleChanged', datetime: new Date().getTime() }),
            );
            this.calculateToken(null, 'role');
            this.redirectToHomePage();
        }),
        catchError((error) => {
            this._changingRole = false;
            return throwError(error);
        }),
    );

    /**
     * Side-effect stream that refreshes organization entities and clears storage cache.
     *
     * Triggered by calling `needUpdateOrganization()`.
     *
     * @returns Observable of loaded entities array.
     */
    updatingOrganization$: Observable<EntData[]> = this.updateOrganization$.pipe(
        withLatestFrom(this.organizationId$, this.organizationInfoId$),
        switchMap(([update, orgId, orgInfoId]) => this.updateOrganizations$(orgId, orgInfoId)),
    );

    /**
     * All roles list stream.
     *
     * @returns Observable of role entities array or `null`.
     */
    roles$: Observable<EntData[]> = this.entityService.getAllEntityList$('permit.role').pipe(
        map((v) => (v ? v.list : null)),
        shareReplay(),
    );

    /**
     * Roles allowed for sign-up.
     *
     * Excludes:
     * - system roles (`is_system`)
     * - director role (`this.role.director`)
     *
     * @returns Observable of role entities array.
     */
    signUpRoles$ = this.roles$.pipe(
        map((roles) => {
            return roles
                .filter((v) => !v.$snapshot.is_system)
                .filter((v) => v.$snapshot.slug !== this.role.director);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Create the service and start internal side-effect streams.
     *
     * This constructor subscribes to `updatingOrganization$` so organization refresh
     * requests are executed when triggered.
     *
     * Injection tokens:
     * - `ROLE`: map of role constants (must include at least `anonymous` and `director` keys used in this service)
     * - `ROLE_ACCOUNT_TYPE`: map from role slug to account type string
     *
     * Provide these tokens in app/module providers when you configure your app:
     *
     * ```ts
     * providers: [
     *   { provide: 'ROLE', useValue: { anonymous: 'anonymous', director: 'director',  ... } },
     *   { provide: 'ROLE_ACCOUNT_TYPE', useValue: { roleSlug: accountType } },
     * ]
     * ```
     *
     * @param entityService - Entity API client used to load/update entities.
     * @param storageService - Storage/cache service used to clear cached entity data.
     * @param srv - Low-level service used for auth and form requests.
     * @param http - Angular `HttpClient` used for some auth flows.
     * @param router - Angular router used for navigation/redirects.
     * @param cookieService - Cookie helper used to clear `bazis_auth` cookie.
     * @param cryptoService - Crypto/signature service used for certificate login.
     * @param modalService - Modal service used to dismiss modals on logout.
     * @param toastService - Toast service used to show sign-up API error messages.
     * @param role - Injected `ROLE` map.
     * @param roleAccountType - Injected `ROLE_ACCOUNT_TYPE` map.
     */
    constructor() {
        this.updatingOrganization$.subscribe();
    }

    /**
     * Clear cached organization entities in storage based on `useOrganizationInfo` flag.
     *
     * @param orgId - Organization id (from `organizationId$`).
     * @param orgInfoId - Organization info id (from `organizationInfoId$`).
     * @returns void
     */
    clearOrganizationInStorage(orgId, orgInfoId) {
        if (this.useOrganizationInfo) {
            this.storageService.clearStorage([
                { entityType: 'organization.organization_info', id: orgId },
                { entityType: 'organization.organization_info', id: orgInfoId },
            ]);
        } else {
            this.storageService.clearStorage([
                { entityType: 'organization.organization', id: orgId },
            ]);
        }
    }

    /**
     * Update stored token and trigger `user$` refresh.
     *
     * Behavior:
     * - When `authResult` is provided, token is stored in localStorage as `access_token`
     * - When route equals `successInvitePageUrl`, token is removed
     * - `notifications` in localStorage is removed when there is no token
     * - `token` holder is updated and `updateUser$` is triggered with `authResult || route || token`
     *
     * @param authResult - Auth response object expected to contain `token` and `user_id` fields. Default: `null`.
     * @param route - Route string used as a trigger value. Default: empty string.
     * @returns void
     */
    calculateToken(authResult = null, route = '') {
        if (authResult) {
            localStorage.setItem('access_token', authResult.token);
        }

        if (route === this.successInvitePageUrl) localStorage.removeItem('access_token');

        const token = localStorage.getItem('access_token');

        if (!token) {
            localStorage.removeItem('notifications');
        }

        this.userRequest$ = null;
        this.token.set(token);
        this.updateUser$.next(authResult || route || token);
    }

    /**
     * Replace access token value in localStorage and in `token` holder.
     *
     * @param token - New access token string.
     * @returns void
     */
    updateToken(token) {
        localStorage.setItem('access_token', token);
        this.token.set(token);
    }

    /**
     * Clear token and related auth data from storage.
     *
     * Side effects:
     * - Removes `access_token` and `notifications` from localStorage
     * - Clears cached `userRequest$`
     * - Deletes `bazis_auth` cookie
     * - Sets `token` holder to empty string
     *
     * @returns void
     */
    resetToken() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('notifications');
        this.userRequest$ = null;
        this.cookieService.delete('bazis_auth', '/');
        this.token.set('');
    }

    /**
     * Navigate to the application home page (`/`).
     *
     * @returns void
     */
    redirectToHomePage() {
        this.router.navigate(['/']);
    }

    /**
     * Login using username/password.
     *
     * This method first obtains an auth token (`getAuthToken$()`), then sends
     * credentials to `authing/password` endpoint and updates session using `calculateToken()`.
     *
     * @param username - User login name.
     * @param password - User password.
     * @returns Observable that emits auth result from backend.
     * @throws Rethrows error after setting `authing$` to false.
     */
    authByPass$(username: string, password: string) {
        this.authing$.next(true);
        return this.getAuthToken$().pipe(
            switchMap((token) =>
                this.srv.sendFormRequest$('authing/password', {
                    username,
                    password,
                }),
            ),
            tap((authResult) => {
                this.calculateToken(authResult);
            }),
            map((authResult) => authResult),
            catchError((e) => {
                this.authing$.next(false);
                return throwError(e);
            }),
        );
    }

    /**
     * Login using electronic signature / certificate.
     *
     * Flow:
     * - Obtain auth token (`getAuthToken$()`)
     * - Decode JWT (`jwt_decode`) and take `sub` claim
     * - Sign `sub` value using provided certificate (`BazisCryptoService`)
     * - Create `authing.certificate` entity with signature
     * - Update session using `calculateToken()`
     *
     * @param certificate - Certificate object used by `BazisCryptoService` (type depends on crypto implementation).
     * @returns Observable that emits auth result from backend.
     */
    authBySignature$(certificate) {
        return this.getAuthToken$().pipe(
            map((token) => {
                const parsedToken: any = jwt_decode(token);
                return parsedToken?.sub;
            }),
            switchMap((str: string) =>
                from(
                    this.cryptoService.getSignForDataWithCertificate(window.btoa(str), certificate),
                ),
            ),
            switchMap((signature) => this.srv.createEntity$('authing.certificate', { signature })),
            tap((authResult: any) => {
                this.calculateToken(authResult);
            }),
            map((authResult) => authResult),
        );
    }

    /**
     * Start ESIA authentication by redirecting browser to backend request URL.
     *
     * Uses current host and protocol and sets `redirect_url` to `successLoginPageUrl`.
     *
     * @returns void
     */
    esiaAuth() {
        location.href = `${location.protocol}//${location.host}/api/web/v1/authing/esia/request_user/?redirect_url=${location.protocol}//${location.host}${this.successLoginPageUrl}`;
    }

    /**
     * Select organization by redirecting to a backend-provided URL.
     *
     * @param url - Backend URL path for organization selection action.
     * @returns void
     */
    selectOrganization(url) {
        location.href = `${location.protocol}//${location.host}${url}?redirect_url=${location.protocol}//${location.host}${this.successLoginPageUrl}`;
    }

    /**
     * Logout request stream.
     *
     * Side effects:
     * - Sets `isLogouting`
     * - Stores `logout` event in localStorage
     * - Calls `afterLogout()` in both success and error paths
     *
     * @returns Observable that emits `'success'` string.
     */
    logout$() {
        this.isLogouting = true;
        localStorage.setItem(
            'event',
            JSON.stringify({ eventName: 'logout', datetime: new Date().getTime() }),
        );
        return this.srv.sendFormRequest$('authing/logout', {}).pipe(
            map((v) => {
                this.isLogouting = false;
                this.afterLogout();
                return 'success';
            }),
            catchError((e) => {
                this.isLogouting = false;
                this.afterLogout();
                return 'success';
            }),
        );
    }

    /**
     * Logout helper that executes `logout$()` once and then redirects to login with return path.
     *
     * @returns void
     */
    logout() {
        this.logout$()
            .pipe(
                take(1),
                tap(() => {
                    this.redirectToLoginWithReturnPath();
                }),
            )
            .subscribe();
    }

    /**
     * Cleanup routine executed after logout request completes.
     *
     * Does nothing while `isLogouting` is true (guard against duplicate calls).
     * Dismisses modals, clears token and storage cache, and then redirects.
     *
     * @returns void
     */
    afterLogout() {
        if (this.isLogouting) return;
        this.modalService.dismiss();
        this.resetToken();
        this.calculateToken();
        this.storageService.clearStorage();
        this.afterLogoutRedirect();
    }

    /**
     * Redirect hook after logout cleanup.
     * Default behavior navigates to home page.
     *
     * @returns void
     */
    afterLogoutRedirect() {
        this.redirectToHomePage();
    }

    /**
     * Redirect to login page and preserve a return path.
     *
     * Does not redirect if current path already contains `authPageUrl`.
     * Stores `urlToRedirectAfterAuth` and passes `returnUrl` as query param.
     *
     * @param url - Return path to save. Default: `location.pathname`.
     * @returns void
     */
    redirectToLoginWithReturnPath(url = location.pathname) {
        if (location.pathname.indexOf(this.authPageUrl) === -1) {
            this.urlToRedirectAfterAuth = url || null;
            this.router.navigate([this.authPageUrl], {
                queryParams: url
                    ? {
                          returnUrl: url,
                      }
                    : null,
            });
        }
    }

    /**
     * Get authentication token used for further auth operations.
     *
     * When backend returns an error with a token in metadata, the token is extracted.
     *
     * @returns Observable that emits token string or `null`.
     */
    getAuthToken$() {
        return this.authRequest$().pipe(
            map((r) => r.token),
            catchError((e) => {
                if (!e?.error.errors || !e.error.errors[0]?.meta?.token) return of(null);
                return of(e.error.errors[0].meta.token);
            }),
        );
    }

    /**
     * Get available authentication types/actions from backend.
     *
     * This method also deletes `bazis_auth` cookie before requesting.
     * When backend returns error with `meta.actions`, it maps actions to a boolean map:
     * `{ [action.code]: true }`.
     *
     * @returns Observable of auth types map object.
     */
    getAuthTypes$() {
        this.cookieService.delete('bazis_auth', '/');
        return this.authRequest$().pipe(
            map((r) => {}),
            catchError((e) => {
                if (!e?.error.errors || !e.error.errors[0]?.meta?.token) return of({});
                return of(
                    e.error.errors[0].meta.actions.reduce(
                        (acc, current) => ({ ...acc, [current.code]: true }),
                        {},
                    ),
                );
            }),
        );
    }

    /**
     * Trigger role change flow.
     *
     * @param payload - Object that contains `roleId` and `userId`.
     * @returns void
     */
    changeRole({ roleId, userId }) {
        this.changeRole$.next({ roleId, userId });
    }

    /**
     * Trigger user refresh.
     *
     * @param data - Trigger value. Common values: `null`, `'socket'`, `'role'`, token string, or `EntData`.
     * @returns void
     */
    needUpdateUser(data = null) {
        this.updateUser$.next(data);
    }

    /**
     * Trigger organization refresh.
     *
     * @returns void
     */
    needUpdateOrganization() {
        this.updateOrganization$.next(true);
    }

    /**
     * Create a new user account (sign-up).
     *
     * On API error:
     * - When `skipErrorToast` is false, shows an error toast and returns `of(null)`
     * - When `skipErrorToast` is true, rethrows the error
     *
     * @param data - Request payload for sign-up form endpoint.
     * @param skipErrorToast - If true, do not show toast and rethrow error. Default: false.
     * @returns Observable that emits backend response or `null` on handled error.
     */
    signUp$(data, skipErrorToast = false) {
        return this.srv.sendFormRequest$('authing/sign_up', data).pipe(
            catchError((e) => {
                if (skipErrorToast) {
                    return throwError(e);
                }

                const message = e?.error?.errors?.[0]?.detail ?? '';

                this.toastService.create({
                    type: 'error',
                    titleKey: 'toast.apiError.title',
                    message,
                });
                return of(null);
            }),
        );
    }

    /**
     * Low-level auth request.
     *
     * @returns Observable of backend auth response (shape defined by API).
     */
    authRequest$() {
        return this.srv.commonGetRequest$('auth');
    }

    /**
     * Resolve organization entity for a given organization id.
     *
     * Uses `getOrganizationEntity$()` when `useOrganizationInfo` is true,
     * otherwise loads `organization.organization` entity directly.
     *
     * @param organizationId - Organization id string.
     * @returns Observable of organization `EntData` or `null` when id is falsy.
     */
    getOrganizationInfo$(organizationId) {
        if (!organizationId) return of(null);
        return this.useOrganizationInfo
            ? this.entityService.getOrganizationEntity$(organizationId)
            : this.entityService.getEntity$('organization.organization', organizationId);
    }

    /**
     * Resolve full organization information for a given organization info id.
     *
     * When `useOrganizationInfo` is false, returns current `organizationInfo$` stream.
     *
     * @param id - Organization info id string.
     * @returns Observable of organization info `EntData` or `null` when id is falsy.
     */
    getOrganizationFullInfo$(id) {
        if (!id) return of(null);
        return this.useOrganizationInfo
            ? this.entityService.getEntity$('organization.organization_info', id)
            : this.organizationInfo$;
    }

    /**
     * Refresh organization entities from API.
     *
     * When `useOrganizationInfo` is false:
     * - Reloads `organization.organization` entity by `orgId`
     *
     * When `useOrganizationInfo` is true:
     * - Reloads `organization.organization_info` entity by `orgInfoId`
     * - Reloads organization entity by `orgId` via `getOrganizationEntity$`
     *
     * @param orgId - Organization id string.
     * @param orgInfoId - Organization info id string.
     * @returns Observable that emits an array of loaded entities.
     */
    updateOrganizations$(orgId, orgInfoId) {
        if (!this.useOrganizationInfo) {
            return forkJoin([
                this.entityService
                    .getEntity$('organization.organization', orgId, { forceLoad: true })
                    .pipe(take(1)),
            ]);
        }

        return forkJoin([
            this.entityService
                .getEntity$('organization.organization_info', orgInfoId, { forceLoad: true })
                .pipe(take(1)),
            this.entityService.getOrganizationEntity$(orgId, true).pipe(take(1)),
        ]);
    }
}
